import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import FormWrapper from 'components/common/formikForm/formWrapper';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import {
    booleanOption,
    businessOperateList,
    organizationIndustryOptions,
    organizationType,
} from 'constant';
import useTranslate from 'hooks/useTranslate';
import { OrganizationInterface } from 'types';
import { generateOptionFromObject, generateOptionsFromArray } from 'utils/dataFormatter';
import {
    organizationInitialValues,
    organizationValidationSchema,
} from 'utils/validations/onboarding';

const organizationTypeOptions = generateOptionFromObject(organizationType);
const businessOperateOptions = generateOptionsFromArray(businessOperateList);
const industryOptions = generateOptionsFromArray(organizationIndustryOptions);
const yesNoOptions = generateOptionFromObject(booleanOption);

interface Props {
    open: boolean;
    disabled?: boolean;
    initialValues?: OrganizationInterface | null;
    onSubmit?: (values: OrganizationInterface) => void;
    onClose: () => void;
}

const fullWidthSx = { gridColumn: '1 / -1' };

const LimaDetailsFields = ({ disabled }: { disabled: boolean }) => {
    const { values, setFieldValue } = useFormikContext<OrganizationInterface>();

    useEffect(() => {
        if (values.applied_for_lmia_prior === false) {
            setFieldValue('has_applied_successfully', null, false);
            setFieldValue('applied_at', '', false);
        }
    }, [setFieldValue, values.applied_for_lmia_prior]);

    if (values.applied_for_lmia_prior !== true) {
        return null;
    }

    return (
        <>
            <FormField
                required
                type="radio"
                size="small"
                name="has_applied_successfully"
                label="Was it successful?"
                options={yesNoOptions}
                disabled={disabled}
            />
            <FormField
                required
                type="date"
                size="small"
                name="applied_at"
                label="When did you apply?"
                disabled={disabled}
            />
        </>
    );
};

const ScrollToFirstError = () => {
    const { submitCount, isValid, isSubmitting, errors } =
        useFormikContext<OrganizationInterface>();
    const lastHandledSubmitCount = useRef(0);

    useEffect(() => {
        if (submitCount === lastHandledSubmitCount.current || isSubmitting) return;

        lastHandledSubmitCount.current = submitCount;
        if (submitCount === 0 || isValid) return;

        const firstErrorField = Object.keys(errors)[0];
        if (!firstErrorField) return;

        const el =
            (document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement | null) ||
            (document.getElementsByName(firstErrorField)[0] as HTMLElement | undefined);

        if (!el) return;

        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof (el as HTMLInputElement).focus === 'function') {
            (el as HTMLInputElement).focus({ preventScroll: true });
        }
    }, [submitCount, isValid, isSubmitting, errors]);

    return null;
};

const OrganizationDetailForm = ({ open, disabled, initialValues, onSubmit, onClose }: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            fullScreen={fullScreen}
            scroll="paper"
            onClose={() => {
                if (!disabled) onClose();
            }}
            slotProps={{
                paper: {
                    sx: {
                        overflow: 'hidden',
                        '& > form': {
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'hidden',
                        },
                    },
                },
            }}
        >
            <FormWrapper<OrganizationInterface>
                enableReinitialize
                initialValues={initialValues || organizationInitialValues}
                validationSchema={organizationValidationSchema}
                onSubmit={(values) => onSubmit?.(values)}
            >
                <ScrollToFirstError />
                <DialogTitle>{translate('editProfile')}</DialogTitle>
                <DialogContent dividers sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
                    <Stack>
                        <Stack spacing={2}>
                            <Typography variant="subtitle1">
                                {translate('organizationIdentification')}
                            </Typography>
                            <FormGridLayout>
                                <FormField
                                    required
                                    size="small"
                                    name="company_name"
                                    label="Business Name"
                                    disabled={disabled}
                                />
                                <FormField
                                    required
                                    size="small"
                                    name="legal_entity_name"
                                    label="Legal name of the business"
                                    disabled={disabled}
                                />
                                <FormField
                                    required
                                    size="small"
                                    name="vat_number"
                                    label="Tax number"
                                    disabled={disabled}
                                />
                                <FormField
                                    required
                                    size="small"
                                    name="address"
                                    label="Business address"
                                    disabled={disabled}
                                />
                                <FormField
                                    size="small"
                                    name="city"
                                    label="city"
                                    disabled={disabled}
                                />
                                <FormField
                                    size="small"
                                    name="zip_code"
                                    label="postalCode"
                                    disabled={disabled}
                                />
                                <FormField
                                    required
                                    type="autosuggest"
                                    size="small"
                                    name="region"
                                    label="Province where the business operates"
                                    options={businessOperateOptions}
                                    disabled={disabled}
                                />
                                <FormField
                                    required
                                    type="monthyear"
                                    size="small"
                                    name="date_established"
                                    label="Year the business was established"
                                    disabled={disabled}
                                />
                                <Box sx={fullWidthSx}>
                                    <FormField
                                        required
                                        type="radio"
                                        size="small"
                                        name="organization_type"
                                        label="What type of organization are you?"
                                        options={organizationTypeOptions}
                                        disabled={disabled}
                                    />
                                </Box>
                            </FormGridLayout>
                        </Stack>

                        <Stack spacing={2}>
                            <Typography variant="subtitle1">
                                {translate('additionalDetails')}
                            </Typography>
                            <FormGridLayout>
                                <FormField
                                    type="autosuggest"
                                    size="small"
                                    name="industry"
                                    label="Industry of the business"
                                    options={industryOptions}
                                    disabled={disabled}
                                />
                                <Box sx={fullWidthSx}>
                                    <FormField
                                        size="small"
                                        name="products_description"
                                        label="Brief description of products or services offered by the business"
                                        multiline
                                        minRows={3}
                                        disabled={disabled}
                                    />
                                </Box>

                                <FormField
                                    required
                                    type="number"
                                    size="small"
                                    name="number_employees"
                                    label="Total number of employees currently working in the business"
                                    disabled={disabled}
                                />
                                <FormField
                                    required
                                    type="number"
                                    size="small"
                                    name="number_canadian_employees"
                                    label="Number of employees who are Canadian citizens or permanent residents"
                                    disabled={disabled}
                                />
                                <FormField
                                    required
                                    type="number"
                                    size="small"
                                    name="annual_revenue"
                                    label="Approximate annual revenue of the business"
                                    disabled={disabled}
                                />
                                <Box sx={fullWidthSx}>
                                    <FormField
                                        required
                                        type="radio"
                                        size="small"
                                        name="operates_from_physical_location"
                                        label="Does the business operate from a physical commercial location?"
                                        options={yesNoOptions}
                                        disabled={disabled}
                                    />

                                    <FormField
                                        required
                                        type="radio"
                                        size="small"
                                        name="has_been_banned_from_hiring_in_canada"
                                        label="Has the business ever been banned or restricted from hiring or sponsoring foreign workers in Canada?"
                                        options={yesNoOptions}
                                        disabled={disabled}
                                    />

                                    <FormField
                                        required
                                        type="radio"
                                        size="small"
                                        name="applied_for_lmia_prior"
                                        label="Has your company ever applied for an LMIA in the past?"
                                        options={yesNoOptions}
                                        disabled={disabled}
                                    />
                                </Box>
                                <LimaDetailsFields disabled={!!disabled} />
                            </FormGridLayout>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button type="button" variant="outlined" onClick={onClose} disabled={disabled}>
                        {translate('cancel')}
                    </Button>
                    <SubmitButton loading={!!disabled}>save</SubmitButton>
                </DialogActions>
            </FormWrapper>
        </Dialog>
    );
};

export default OrganizationDetailForm;
